export function fromSmallestUnits(units: string, decimals: number): string {
  const normalized = units.replace(/^0+/, '') || '0'
  if (decimals === 0) return normalized
  const padded = normalized.padStart(decimals + 1, '0')
  const integerPart = padded.slice(0, -decimals)
  const rawFraction = padded.slice(-decimals)
  let end = rawFraction.length
  while (end > 0 && rawFraction[end - 1] === '0') end--
  const fractionPart = rawFraction.slice(0, end)
  return fractionPart ? `${integerPart}.${fractionPart}` : integerPart
}

export type DecimalInputClassification =
  | { kind: 'empty' }
  | { kind: 'incomplete' }
  | { kind: 'excess_precision' }
  | { kind: 'invalid' }
  | { kind: 'valid'; units: bigint }

const CANONICAL_UINT_PATTERN = /^(0|[1-9]\d*)$/
const CANONICAL_FRACTION_PATTERN = /^\d+$/

const hasValidDecimals = (decimals: number): boolean =>
  Number.isSafeInteger(decimals) && decimals >= 0 && decimals <= 255

const splitCanonicalDecimal = (value: string): { integer: string; fraction: string } | null => {
  const parts = value.split('.')
  if (parts.length > 2) return null

  const integer = parts[0] ?? ''
  const fraction = parts[1] ?? ''
  if (!CANONICAL_UINT_PATTERN.test(integer)) return null
  if (parts.length === 2 && !CANONICAL_FRACTION_PATTERN.test(fraction)) return null
  return { integer, fraction }
}

export function classifyDecimalInput(amount: string, decimals: number): DecimalInputClassification {
  if (!hasValidDecimals(decimals)) return { kind: 'invalid' }

  if (!amount) return { kind: 'empty' }
  if (amount.endsWith('.') && CANONICAL_UINT_PATTERN.test(amount.slice(0, -1))) {
    return { kind: 'incomplete' }
  }

  const decimal = splitCanonicalDecimal(amount)
  if (!decimal) return { kind: 'invalid' }

  const { integer, fraction } = decimal
  if (fraction.length > decimals) return { kind: 'excess_precision' }

  const raw = `${integer}${fraction.padEnd(decimals, '0')}`.replace(/^0+/, '') || '0'
  return { kind: 'valid', units: BigInt(raw) }
}

export function toSmallestUnits(amount: string, decimals: number): string {
  const classified = classifyDecimalInput(amount, decimals)
  if (classified.kind !== 'valid') {
    throw new Error(`invalid_decimal_amount:${classified.kind}`)
  }
  return classified.units.toString()
}

export function parseDecimalUnits(amount: string, decimals: number): bigint | null {
  const classified = classifyDecimalInput(amount, decimals)
  return classified.kind === 'valid' ? classified.units : null
}

const UINT64_MAX = 18_446_744_073_709_551_615n

export const atomicProviderAmountToDecimal = (
  value: string | null | undefined,
  decimals: number
): string | undefined => {
  if (!value || !CANONICAL_UINT_PATTERN.test(value) || !Number.isSafeInteger(decimals)) {
    return undefined
  }
  if (decimals < 0 || BigInt(value) > UINT64_MAX) return undefined
  const amount = fromSmallestUnits(value, decimals)
  if (!/^(0|[1-9]\d*)(\.\d+)?$/.test(amount) || amount.length > 32) return undefined
  return amount
}

export function withSafetyBps(amount: string, bps: bigint): string {
  try {
    const value = BigInt(amount)
    const adjusted = (value * bps) / 10_000n
    return adjusted > 0n ? adjusted.toString() : value.toString()
  } catch {
    return amount
  }
}
export const TOKEN_DECIMALS = {
  USDT: 6,
  USDC: 6,
  TON: 9,
  SOL: 9,
  SPOTTY: 9,
  ETH: 18,
} as const
